import { v4 as uuidv4 } from 'uuid'
import { RectGrid } from './RectGrid'
import { ObjectGrid } from './ObjectGrid'
import { CircleGrid } from './CircleGrid'
import { TextGrid } from './TextGrid'
import { ImageGrid } from './ImageGrid'
import { TriangleGrid } from './TriangleGrid'
import { CanvasGrid } from './CanvasGrid'
import { UndoButton } from './ui/UndoButton'
import { RedoButton } from './ui/RedoButton'
import Sheet from '@mui/joy/Sheet'

export const ObjectPanel = ({ canvas, obj, onDelete, onChange }) => {
    const handleChange = () => {
        onChange(canvas)
    }

    const handleOpacityChange = ({ obj, value }) => {
        obj.set('opacity', value)
        canvas.renderAll()
        handleChange()
    }

    const handleTextAlignChange = ({ obj, value }) => {
        obj.set('textAlign', value)
        canvas.renderAll()
        handleChange()
    }

    const handleFontSizeChange = ({ obj, value }) => {
        obj.set('fontSize', value)
        canvas.renderAll()
        handleChange()
    }

    const handleBorderRadiusChange = ({ obj, value }) => {
        obj.set({ rx: value, ry: value })
        canvas.renderAll()
        handleChange()
    }

    const handleFillColorChange = ({ obj, value }) => {
        obj.set({ fill: value })
        canvas.renderAll()
        handleChange()
    }

    const handleFontChange = ({ obj, value }) => {
        obj.set({ fontFamily: value })
        canvas.renderAll()
        handleChange()
    }

    const handleFontWeightChange = ({ obj, value }) => {
        console.log('font weight change')
        obj.set({ fontWeight: value })
        canvas.renderAll()
        handleChange()
    }

    const handleLayerUp = (obj) => {
        obj.bringForward()
        canvas.renderAll()
        handleChange()
    }

    const handleLayerDown = (obj) => {
        obj.sendBackwards()
        canvas.renderAll()
        handleChange()
    }

    const handleTextChange = (obj) => {
        canvas.renderAll()
        handleChange()
    }

    const handleCanvasColorChange = ({ canvas, value }) => {
        canvas.setBackgroundColor(value)
        canvas.renderAll()
        handleChange()
    }

    const createGrid = (child, obj) => (
        <ObjectGrid
            key={uuidv4()}
            onOpacityChange={({ obj, value }) =>
                handleOpacityChange({ obj, value })
            }
            onFillColorChange={handleFillColorChange}
            obj={obj}
            onLayerUp={handleLayerUp}
            onLayerDown={handleLayerDown}
            onDelete={handleDelete}
        >
            {child}
        </ObjectGrid>
    )

    const createRectGrid = (obj) => {
        return createGrid(
            <RectGrid
                onBorderRadiusChange={handleBorderRadiusChange}
                rect={obj}
            />,
            obj
        )
    }

    const createCircleGrid = (obj) => {
        return createGrid(<CircleGrid />, obj)
    }

    const createTextGrid = (obj) => {
        return createGrid(
            <TextGrid
                onFontChange={handleFontChange}
                onFontSizeChange={handleFontSizeChange}
                onTextChange={handleTextChange}
                onWeightChange={handleFontWeightChange}
                onTextAlignChange={handleTextAlignChange}
                textObj={obj}
            />,
            obj
        )
    }

    const createTriangleGrid = (obj) => {
        return createGrid(<TriangleGrid />, obj)
    }

    const createImageGrid = (obj) => {
        return createGrid(<ImageGrid />, obj)
    }

    const PANEL_DICT = {
        rect: createRectGrid,
        'template-i-text': createTextGrid,
        'i-text': createTextGrid,
        textbox: createTextGrid,
        'template-textbox': createTextGrid,
        'prompt-textbox': createTextGrid,
        circle: createCircleGrid,
        triangle: createTriangleGrid,
        image: createImageGrid,
    }

    const handleDelete = ({ obj }) => {
        canvas.remove(obj)
        canvas.renderAll()
        onDelete()
    }

    return (
        <Sheet
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                borderWidth: '0 0 1px 0',
                p: 1,
                gap: 1,
                alignItems: 'flex-end',
            }}
            color="neutral"
        >
            {canvas && (
                <>
                    <UndoButton
                        onClick={canvas.undo.bind(canvas)}
                        disabled={!canvas.canUndo()}
                    />
                    <RedoButton
                        onClick={canvas.redo.bind(canvas)}
                        disabled={!canvas.canRedo()}
                    />
                </>
            )}
            {obj ? (
                PANEL_DICT[obj.type](obj)
            ) : (
                <CanvasGrid
                    canvas={canvas}
                    onColorChange={handleCanvasColorChange}
                />
            )}
        </Sheet>
    )
}
